import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiBuilding, FiMapPin, FiCalendar, FiFileText, FiSend } = FiIcons;

const LetterForm = ({ onSubmit, initialData }) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Recipient name is required';
    }
    if (!formData.office.trim()) {
      newErrors.office = 'Office/Organization is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City/Community is required';
    }
    if (!formData.agentName.trim()) {
      newErrors.agentName = 'Agent name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const inputClasses = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-xl p-8"
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          Generate Community Outreach Letter
        </h2>
        <p className="text-gray-600">
          Create professional letters to promote financial education workshops
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Language Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelClasses}>
              <SafeIcon icon={FiFileText} className="inline mr-2" />
              Language / Idioma
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="spanish">🇪🇸 Español</option>
              <option value="english">🇺🇸 English</option>
            </select>
          </div>

          <div>
            <label className={labelClasses}>
              <SafeIcon icon={FiCalendar} className="inline mr-2" />
              Date / Fecha
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
        </div>

        {/* Recipient Information */}
        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">
            Recipient Information / Información del Destinatario
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                <SafeIcon icon={FiUser} className="inline mr-2" />
                Recipient Name / Nombre del Destinatario *
              </label>
              <input
                type="text"
                name="recipientName"
                value={formData.recipientName}
                onChange={handleChange}
                placeholder="e.g., Sra. María González"
                className={`${inputClasses} ${errors.recipientName ? 'border-red-500' : ''}`}
              />
              {errors.recipientName && (
                <p className="text-red-500 text-sm mt-1">{errors.recipientName}</p>
              )}
            </div>

            <div>
              <label className={labelClasses}>
                <SafeIcon icon={FiBuilding} className="inline mr-2" />
                Office / Organization *
              </label>
              <input
                type="text"
                name="office"
                value={formData.office}
                onChange={handleChange}
                placeholder="e.g., Oficina de Asuntos Comunitarios de Newark"
                className={`${inputClasses} ${errors.office ? 'border-red-500' : ''}`}
              />
              {errors.office && (
                <p className="text-red-500 text-sm mt-1">{errors.office}</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className={labelClasses}>
              <SafeIcon icon={FiMapPin} className="inline mr-2" />
              City / Community *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="e.g., Newark, NJ"
              className={`${inputClasses} ${errors.city ? 'border-red-500' : ''}`}
            />
            {errors.city && (
              <p className="text-red-500 text-sm mt-1">{errors.city}</p>
            )}
          </div>
        </div>

        {/* Agent Information */}
        <div className="bg-green-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800 mb-4">
            Agent Information / Información del Agente
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                <SafeIcon icon={FiUser} className="inline mr-2" />
                Agent / Volunteer Name *
              </label>
              <input
                type="text"
                name="agentName"
                value={formData.agentName}
                onChange={handleChange}
                placeholder="e.g., Sebastián Rodríguez"
                className={`${inputClasses} ${errors.agentName ? 'border-red-500' : ''}`}
              />
              {errors.agentName && (
                <p className="text-red-500 text-sm mt-1">{errors.agentName}</p>
              )}
            </div>

            <div>
              <label className={labelClasses}>
                Agent Title (Optional)
              </label>
              <input
                type="text"
                name="agentTitle"
                value={formData.agentTitle}
                onChange={handleChange}
                placeholder="e.g., Prosperity Leaders Representative"
                className={inputClasses}
              />
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div>
          <label className={labelClasses}>
            Additional Notes / Notas Adicionales (Optional)
          </label>
          <textarea
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleChange}
            rows="4"
            placeholder="Add any additional information or specific requests..."
            className={inputClasses}
          />
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg flex items-center justify-center space-x-2"
        >
          <SafeIcon icon={FiSend} className="text-xl" />
          <span>Generate PDF Letter</span>
        </motion.button>
      </form>
    </motion.div>
  );
};

export default LetterForm;